import { userIdToJsonLogic } from '@pbk-web/member-assignment';
import type { ClientFormTag } from '@pbk-web/types';

import { GraphQLRoleToPlatformRole } from '../components/table-view/data-renderers/MembersRenderer';
import type { ClientFormUserRoleValues } from './ClientFormUserRoles';
import { ClientFormUserRole } from './ClientFormUserRoles';
import type User from './User';
import type { MinimalUser } from './User';

export interface ClientFormRoleRequiredness {
  role: ClientFormUserRoleValues;
  requiresAction?: boolean | null;
}

export interface ClientFormUser extends MinimalUser {
  role: ClientFormUserRoleValues;
  requiresAction?: boolean | null;
  sortOrder?: number | null;
  statusUtc?: string | null;
  acceptedStatus?: boolean;
  formSectionId?: string | null;
  addedAsIndividual?: boolean;
  automationId?: string;
  addedByAutomation?: boolean;
  tagIds?: string[];
  userId?: string;
  /** FE ONLY — set when this member was resolved from a criteria access-policy rule rather than
   *  assigned individually. Read paths (role resolution, membership, display) should include these;
   *  write-back/editor paths must EXCLUDE them so the criteria policy isn't rewritten as individuals. */
  fromCriteria?: boolean;

  user?: MinimalUser; // Mapped onto this object
  otherRoles?: ClientFormRoleRequiredness[]; // FE ONLY
}
export type ClientFormUserOnly = Omit<ClientFormUser, 'role' | 'id' | 'requiresAction'> &
  Partial<Pick<ClientFormUser, 'role' | 'id' | 'requiresAction'>>;

/**
 * Owners matched by a criteria access-policy collapse to a SINGLE effective owner: the resolved
 * user whose id sorts first as a string. Only that user gets owner access and is listed outside
 * the member-edit modal's expanded criteria group. Returns undefined for an empty match set.
 */
export const getEffectiveCriteriaOwnerId = (resolvedUserIds: readonly string[]): string | undefined => [...resolvedUserIds].sort()[0];

export type { ClientFormTag } from '@pbk-web/types';

export type ClientFormUserRequest = {
  userId: string;
  addedAsIndividual: boolean;
  requiresAction: boolean | null;
  sortOrder: number | null;
  tagIds: string[];
  formSectionId?: string | null;

  user?: User; // Mapped onto this object
  role?: ClientFormUserRoleValues; // Mapped onto this object
};

export const clientFormUsersToRequestDto = (users: (ClientFormUser | ClientFormUserRequest)[], tags: ClientFormTag[]): ClientFormUserDto[] => {
  return Object.values(ClientFormUserRole).map((role) => ({
    role,
    users: users
      .filter((user) => user.role === role)
      .map((user) => ({
        userId: 'userId' in user ? (user.userId ?? '') : user.id,
        addedAsIndividual: user.addedAsIndividual ?? false,
        requiresAction: user.requiresAction ?? null,
        sortOrder: user.sortOrder ?? null,
        tagIds: user.tagIds || [],
        formSectionId: user.formSectionId ?? null,
      })),
    tags: tags.filter((tag) => tag.role === role),
  }));
};

export interface ClientFormUserDto {
  role: ClientFormUserRoleValues;
  users: ClientFormUserRequest[];
  tags: ClientFormTag[];
}

// GET /v1/forms/access/{formId} response types
export interface FormAccessPolicyRuleMetaData {
  sortOrder?: number | null;
  requiresAction?: boolean | null;
  individualUser?: string | null;
  /** User-provided rule group title for criteria policies. */
  name?: string | null;
  /** True when an automation added this member — lets the UI show the automation icon even when the
   *  automation list can't be fetched (insufficient permissions). */
  addedByAutomation?: boolean;
}

// policy/metadata arrive as single values on seeded data but as arrays on rules saved by the app
export interface FormAccessPolicyRule {
  policy: string | object | (string | object)[];
  metadata?: FormAccessPolicyRuleMetaData | FormAccessPolicyRuleMetaData[];
  resolvedUsers: string[];
}

export interface FormAccessRoleGroup {
  role: ClientFormUserRoleValues;
  policyRules: FormAccessPolicyRule[];
}

export interface FormAccessResponse {
  formUsers: FormAccessRoleGroup[];
  sectionUsers: Record<string, FormAccessRoleGroup[]>;
}

// GET /v1/forms/access/{formId}/snapshot response types.
// Once a form leaves draft the access list is frozen into a snapshot; the left-nav user list reads
// from this instead of the live access-policy. `role` is the numeric REST role (10=Viewer … 50=Owner).
export interface FormAccessSnapshotUser {
  id: string;
  resourceId: string;
  userId: string;
  role: ClientFormUserRoleValues;
  requiresAction?: boolean | null;
  sortOrder?: number | null;
  formSectionId?: string | null;
  acceptedStatus?: boolean;
  statusUtc?: string | null;
}

export interface FormAccessSnapshotResponse {
  snapshotUsers: FormAccessSnapshotUser[];
}

/**
 * GET /v1/clients/{clientId}/template/forms/{formId}/access — resource (template form) access policy.
 * Flat: resource access has no section scoping, so there is no `sectionUsers` map. Each entry in
 * `members` is a role group whose rules carry the users they currently resolve to.
 */
export interface TemplateFormAccessResponse {
  members: FormAccessRoleGroup[];
}

// POST /v1/forms/access/{formId} request types
export interface FormAccessPolicyMetaData {
  sortOrder: number | null;
  requiresAction: boolean | null;
  individualUser?: string | null;
  /** User-provided rule group title for criteria policies. */
  name?: string | null;
}

export interface FormAccessPolicy {
  rule: object[];
  metaData: FormAccessPolicyMetaData;
}

export interface FormAccessRoleGroupRequest {
  role: ClientFormUserRoleValues;
  parentResourceId: string | null;
  policies: FormAccessPolicy[];
}

export const clientFormUsersToAccessRequestDto = (
  users: (ClientFormUser | ClientFormUserRequest)[],
  tags: ClientFormTag[],
  parentResourceId?: string | null,
): FormAccessRoleGroupRequest[] => {
  return Object.values(ClientFormUserRole).map((role) => ({
    role,
    parentResourceId: parentResourceId ?? null,
    policies: [
      ...users
        .filter((user) => user.role === role)
        .flatMap((user) => {
          const userId = user.userId ?? ('id' in user ? user.id : undefined);
          if (!userId) return [];
          return [
            {
              rule: [userIdToJsonLogic(userId)],
              metaData: { sortOrder: user.sortOrder ?? null, requiresAction: user.requiresAction ?? null, individualUser: userId },
            },
          ];
        }),
      ...tags
        .filter((tag) => tag.role === role && tag.criteria)
        .flatMap((tag) => {
          try {
            return [
              {
                rule: [JSON.parse(tag.criteria!) as object],
                metaData: { requiresAction: tag.requiresAction ?? null, sortOrder: tag.sortOrder ?? null, name: tag.name?.trim() || null },
              },
            ];
          } catch {
            return []; // skip malformed criteria instead of failing the whole save
          }
        }),
    ],
  }));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const GQLUserToClientFormUserDto = (users: any[]): ClientFormUserDto[] => {
  const result: ClientFormUserDto[] = [];

  Object.values(ClientFormUserRole).forEach((role) => {
    const usersForRole = users
      .filter((user) => GraphQLRoleToPlatformRole[user.role] === role)
      .map((user) => ({
        ...user.pbkUser,
        role,
        userId: user.userId,
        addedAsIndividual: user.addedAsIndividual,
        requiresAction: user.requiresAction,
        sortOrder: user.sortOrder,
        tagIds: [],
        formSectionId: user.formSectionId || null,
      }));

    result.push({
      role,
      users: usersForRole,
      tags: [],
    });
  });

  return result;
};
